import React, { useState } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import useMerchants from '../../hooks/useMerchants';
import { useTranslation } from 'react-i18next';

import { BookOpenIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import Table from '../shared/Table';
import ReusableButton from '../shared/ReusableButton';

const ITEMS_PER_PAGE = 10;

interface Merchant {
  id: number;
  company: string;
  industry: string;
  contactPerson: string;
  email: string;
  sourceDomain: string;
  createdAt: string;
}

const Merchants: React.FC = () => {
  const { t } = useTranslation();
  const { merchants, loading, error } = useMerchants();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const navigate = useNavigate();

  const handleView = async (id: number) => {
    navigate(`/merchants/${id}`);
  };

  const key = (merchant: Merchant) => merchant.id;
  const row = (merchant: Merchant) => (
    <>
      <td className="border px-2 py-2 capitalize break-words">{merchant.company}</td>
      <td className="border px-2 py-2 break-words">
        <span className="capitalize">{merchant.contactPerson}</span>
        <br />
        {merchant.email ? (
          <a
            href={`mailto:${merchant.email}`}
            className="text-blue-600 visited:text-purple-600 underline underline-offset-2 hover:text-blue-400 break-all"
          >
            <span className="lowercase">{merchant.email}</span>
          </a>
        ) : (
          'N/A'
        )}
      </td>
      <td className="border px-2 py-2 break-words">{merchant.industry ? merchant.industry : '-'}</td>
      <td className="border px-2 py-2 whitespace-nowrap w-28">{format(new Date(merchant.createdAt), 'MMM d, yyyy')}</td>
      <td className="border px-2 py-2 w-24">
        <ReusableButton
          onClick={() => handleView(merchant.id)}
          icon={<BookOpenIcon className="size-5" aria-hidden="true" />}
          label={t('common.view')}
          className="my-1"
        />
      </td>
    </>
  );

  // calculate pagination
  const totalPages = Math.ceil(merchants.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const currentMerchants = merchants.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900">{t('merchants.title')}</h1>
        <p className="text-slate-600 mt-1">{t('merchants.subtitle')}</p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-2 text-slate-600">{t('common.loading')}</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-700">{t('common.error')}: {error}</p>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {merchants.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <Table
                  headers={[
                    t('merchant.company'),
                    t('merchant.contact'),
                    t('merchant.industry'),
                    t('common.createdAt'),
                    t('common.actions'),
                  ]}
                  data={currentMerchants}
                  renderRow={row}
                  keyExtractor={key}
                />
              </div>

              {/* Pagination */}
              {merchants.length > ITEMS_PER_PAGE && (
                <div className="border-t border-slate-200 px-6 py-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-slate-600">
                      Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, merchants.length)} of {merchants.length} merchants
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                        className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t('common.previous')}
                      >
                        <ChevronLeftIcon className="h-5 w-5 text-slate-600" />
                      </button>
                      <span className="text-sm font-medium text-slate-700 px-4">
                        {t('common.page')} {currentPage} {t('common.of')} {totalPages}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title={t('common.next')}
                      >
                        <ChevronRightIcon className="h-5 w-5 text-slate-600" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
              <BuildingStorefrontIcon className="mx-auto h-12 w-12 text-slate-400" />
              <h3 className="mt-4 text-lg font-semibold text-slate-900">{t('merchants.noMerchantsFound')}</h3>
              <p className="mt-2 text-slate-600">No merchant accounts have been created yet</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Merchants;
